import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import api from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Plus, Wallet } from "lucide-react";
import { fmtDateTime } from "@/lib/format";
import { Money, TxList, TopupBadge, ReceiptLink, METHOD_LABEL } from "@/components/wallet/shared";
import TopupRequestDialog from "@/components/wallet/TopupRequestDialog";
import UnitsChips from "@/components/wallet/UnitsChips";
import { useWalletPackages, PackageLabel } from "@/components/wallet/PackagesDialog";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";

function BuyPackages({ balance }) {
  const qc = useQueryClient();
  const { data: pkgs = [] } = useWalletPackages();
  const mut = useMutation({
    mutationFn: async (id) => (await api.post(`/wallet/me/packages/${id}/buy`)).data,
    onSuccess: () => { toast.success("Pacchetto acquistato"); qc.invalidateQueries({ queryKey: ["my-wallet"] }); },
    onError: (e) => toast.error(e.response?.data?.detail || "Errore"),
  });
  if (!pkgs.length) return null;
  return (
    <Card className="p-5 border border-stone-200 rounded-xl shadow-none mb-6" data-testid="my-wallet-packages">
      <div className="text-sm font-medium mb-2">Pacchetti prepagati</div>
      <ul className="divide-y divide-stone-100">
        {pkgs.map((p) => (
          <li key={p.id} className="py-2.5 flex items-center gap-3 text-sm">
            <span className="flex-1"><PackageLabel p={p}/></span>
            <Button size="sm" variant="outline" className="rounded-full" disabled={balance < p.price || mut.isPending}
              onClick={() => { if (window.confirm(`Acquistare "${p.name}" per € ${p.price.toFixed(2)}?`)) mut.mutate(p.id); }} data-testid={`buy-package-${p.id}`}>
              {balance < p.price ? "Credito insufficiente" : "Acquista"}
            </Button>
          </li>
        ))}
      </ul>
    </Card>
  );
}

export default function MyWallet() {
  const [open, setOpen] = useState(false);
  const { data, isLoading, error } = useQuery({ queryKey: ["my-wallet"], queryFn: async () => (await api.get("/wallet/me")).data });
  if (isLoading) return <div className="p-10 text-center text-sm text-stone-400">Caricamento…</div>;
  if (error) return <div className="p-10 text-center text-sm text-stone-500">{error.response?.data?.detail || "Wallet non disponibile"}</div>;
  const pending = (data.topups || []).filter((t) => t.status === "pending");

  return (
    <div className="p-4 sm:p-6 md:p-10 max-w-[1000px] mx-auto">
      <PageHeader title="Il mio wallet" subtitle="Credito virtuale per gli acquisti interni alla scuola."/>
      <Card className="p-6 rounded-2xl border-0 shadow-none bg-gradient-to-br from-[#0F766E] to-[#134E4A] text-white mb-6 relative overflow-hidden">
        <Wallet size={120} className="absolute -right-6 -bottom-6 opacity-10"/>
        <div className="text-[11px] uppercase tracking-widest opacity-75">Saldo disponibile</div>
        <div className="text-4xl font-semibold mt-1" data-testid="my-wallet-balance"><Money value={data.balance} className={data.balance < 0 ? "!text-red-200" : ""}/></div>
        {pending.length > 0 && <div className="text-xs opacity-85 mt-2" data-testid="my-wallet-pending">{pending.length} ricarica/e in attesa di verifica</div>}
        <Button onClick={() => setOpen(true)} className="mt-4 rounded-full bg-white text-[#0F766E] hover:bg-stone-100" data-testid="my-wallet-topup-btn"><Plus size={14} className="mr-1.5"/> Richiedi ricarica</Button>
      </Card>
      {data.units?.length > 0 && <div className="mb-6"><UnitsChips units={data.units} testId="my-wallet-units"/></div>}
      <BuyPackages balance={data.balance}/>

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
        <Card className="p-5 border border-stone-200 rounded-xl shadow-none lg:col-span-3">
          <div className="text-sm font-medium mb-2">Movimenti</div>
          <TxList transactions={data.transactions}/>
        </Card>
        <Card className="p-5 border border-stone-200 rounded-xl shadow-none lg:col-span-2">
          <div className="text-sm font-medium mb-2">Le mie richieste di ricarica</div>
          {!data.topups?.length && <div className="text-sm text-stone-400 py-4 text-center">Nessuna richiesta.</div>}
          <ul className="divide-y divide-stone-100">
            {(data.topups || []).map((t) => (
              <li key={t.id} className="py-2.5 text-sm" data-testid={`my-topup-${t.id}`}>
                <div className="flex items-center justify-between gap-2"><Money value={t.amount} className="font-semibold"/><TopupBadge status={t.status}/></div>
                <div className="text-[11px] text-stone-500">{METHOD_LABEL[t.method]} · {fmtDateTime(t.created_at)}{t.decision_note && ` · ${t.decision_note}`}</div>
                <ReceiptLink topup={t}/>
              </li>
            ))}
          </ul>
        </Card>
      </div>
      <TopupRequestDialog open={open} onOpenChange={setOpen}/>
    </div>
  );
}
